import { create } from 'zustand';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type {
  BloodType,
  BloodTypeRh,
  EducationLevel,
  MaritalStatus,
  Ancestry,
  BodyType,
  HairType,
  HairColor,
  Eyelid,
  DonationMethod,
  IdentityDisclosure,
  PreferredLocation,
} from '@kulum/shared';

// Annual Income type
export type AnnualIncome = 
  | '400万円未満'
  | '400万円〜600万円'
  | '600万円〜800万円'
  | '800万円〜1000万円'
  | '1000万円〜1500万円'
  | '1500万円〜2000万円'
  | '2000万円以上';

// Profile data types
export interface ProfileBasicInfo {
  birthDate?: Date;
  nationality?: string;
  bloodType?: BloodType;
  bloodTypeRh?: BloodTypeRh;
  birthplace?: string;
  residence?: string;
  education?: {
    level?: EducationLevel;
    schoolName?: string;
    faculty?: string;
    status?: '卒業' | '在学中' | '中退';
  };
  annualIncome?: AnnualIncome;
  maritalStatus?: MaritalStatus;
  children?: {
    sons: number;
    daughters: number;
  };
  ancestry?: Ancestry;
  vaccinations?: {
    influenza?: string;
    covid?: string;
  };
}

export interface ProfilePhysicalInfo {
  height?: number;
  weight?: number;
  bodyType?: BodyType;
  hairType?: HairType;
  hairColor?: HairColor;
  eyelid?: Eyelid;
}

export interface ProfileDonationPolicy {
  methods?: DonationMethod[];
  compensation?: number;
  travelExpense?: 'ドナーが負担' | 'ドニーが負担' | '双方で負担';
  identityDisclosure?: IdentityDisclosure;
  consanguinityMeasures?: '対策を講じている' | '講じていない';
  rightToKnowOrigin?: '対応可能' | '対応不可能';
  sameCityFamilyDonation?: '賛同する' | '賛同しない';
  donationAreas?: string[];
}

export interface ProfileDoneePolicy {
  methods?: DonationMethod[];
  preferredLocation?: PreferredLocation;
}

// 公開プロフィール（profiles コレクション）
export interface PublicProfileData {
  userId: string;
  basicInfo: ProfileBasicInfo;
  physicalInfo?: ProfilePhysicalInfo;
  donationPolicy?: ProfileDonationPolicy;
  doneePolicy?: ProfileDoneePolicy;
  freeText?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// 非公開プロフィール（privateProfiles コレクション）
export interface PrivateProfileData {
  userId: string;
  freeText?: string;
  images?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

// 結合プロフィール（アプリ内で使用）
export interface ProfileData {
  userId: string;
  basicInfo: ProfileBasicInfo;
  physicalInfo?: ProfilePhysicalInfo;
  donationPolicy?: ProfileDonationPolicy;
  doneePolicy?: ProfileDoneePolicy;
  freeText?: string;
  images?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

interface ProfileState {
  profile: ProfileData | null;
  isLoading: boolean;
  isSaving: boolean;
  isUploading: boolean;
  error: string | null;

  // Actions
  fetchProfile: (userId: string) => Promise<void>;
  fetchPublicProfile: (userId: string) => Promise<PublicProfileData | null>;
  fetchPrivateProfile: (userId: string) => Promise<PrivateProfileData | null>;
  updateBasicInfo: (userId: string, basicInfo: Partial<ProfileBasicInfo>) => Promise<void>;
  updatePhysicalInfo: (userId: string, physicalInfo: Partial<ProfilePhysicalInfo>) => Promise<void>;
  updateDonationPolicy: (userId: string, donationPolicy: Partial<ProfileDonationPolicy>) => Promise<void>;
  updateDoneePolicy: (userId: string, doneePolicy: Partial<ProfileDoneePolicy>) => Promise<void>;
  updateFreeText: (userId: string, freeText: string) => Promise<void>;
  uploadProfileImage: (userId: string, file: File) => Promise<string>;
  uploadAdditionalImage: (userId: string, file: File) => Promise<string>;
  removeAdditionalImage: (userId: string, imageUrl: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// Helper to convert Firestore Timestamp to Date
const timestampToDate = (timestamp: Timestamp | undefined): Date | undefined => {
  return timestamp?.toDate();
};

// Helper to convert Date to Firestore Timestamp
const dateToTimestamp = (date: Date | undefined): Timestamp | undefined => {
  return date ? Timestamp.fromDate(date) : undefined;
};

export const useProfileStore = create<ProfileState>((set, get) => ({
  profile: null,
  isLoading: false,
  isSaving: false,
  isUploading: false,
  error: null,

  // 公開＋非公開プロフィールを結合して取得（自分用）
  fetchProfile: async (userId: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // 公開プロフィールを取得
      const profileRef = doc(db, 'profiles', userId);
      const profileSnap = await getDoc(profileRef);

      // 非公開プロフィールを取得
      const privateProfileRef = doc(db, 'privateProfiles', userId);
      const privateProfileSnap = await getDoc(privateProfileRef);

      let publicData: Partial<PublicProfileData> = {};
      let privateData: Partial<PrivateProfileData> = {};

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        publicData = {
          basicInfo: {
            ...data.basicInfo,
            birthDate: timestampToDate(data.basicInfo?.birthDate),
          },
          physicalInfo: data.physicalInfo,
          donationPolicy: data.donationPolicy,
          doneePolicy: data.doneePolicy,
          freeText: data.freeText,
          createdAt: timestampToDate(data.createdAt),
          updatedAt: timestampToDate(data.updatedAt),
        };
      }

      if (privateProfileSnap.exists()) {
        const data = privateProfileSnap.data();
        privateData = {
          freeText: data.freeText,
          images: data.images || [],
        };
      }

      set({
        profile: {
          userId,
          basicInfo: publicData.basicInfo || {},
          physicalInfo: publicData.physicalInfo,
          donationPolicy: publicData.donationPolicy,
          doneePolicy: publicData.doneePolicy,
          // freeText は公開プロフィールに格納（旧データ互換で private もフォールバック）
          freeText: publicData.freeText ?? privateData.freeText,
          images: privateData.images || [],
          createdAt: publicData.createdAt,
          updatedAt: publicData.updatedAt,
        },
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      set({ error: 'プロフィールの取得に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  // 公開プロフィールのみ取得（検索・詳細表示用）
  fetchPublicProfile: async (userId: string): Promise<PublicProfileData | null> => {
    try {
      const profileRef = doc(db, 'profiles', userId);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        return {
          userId,
          basicInfo: {
            ...data.basicInfo,
            birthDate: timestampToDate(data.basicInfo?.birthDate),
          },
          physicalInfo: data.physicalInfo,
          donationPolicy: data.donationPolicy,
          doneePolicy: data.doneePolicy,
          freeText: data.freeText,
          createdAt: timestampToDate(data.createdAt),
          updatedAt: timestampToDate(data.updatedAt),
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching public profile:', error);
      return null;
    }
  },

  // 非公開プロフィールのみ取得（閲覧申請承認後）
  fetchPrivateProfile: async (userId: string): Promise<PrivateProfileData | null> => {
    try {
      const privateProfileRef = doc(db, 'privateProfiles', userId);
      const privateProfileSnap = await getDoc(privateProfileRef);

      if (privateProfileSnap.exists()) {
        const data = privateProfileSnap.data();
        return {
          userId,
          freeText: data.freeText,
          images: data.images || [],
          createdAt: timestampToDate(data.createdAt),
          updatedAt: timestampToDate(data.updatedAt),
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching private profile:', error);
      return null;
    }
  },

  updateBasicInfo: async (userId: string, basicInfo: Partial<ProfileBasicInfo>) => {
    try {
      set({ isSaving: true, error: null });
      
      const profileRef = doc(db, 'profiles', userId);
      const currentProfile = get().profile;

      // Convert Date to Timestamp for Firestore
      const firestoreBasicInfo = {
        ...basicInfo,
        birthDate: dateToTimestamp(basicInfo.birthDate),
      };

      // Check if document exists
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        await updateDoc(profileRef, {
          basicInfo: {
            ...currentProfile?.basicInfo,
            ...firestoreBasicInfo,
          },
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(profileRef, {
          userId,
          basicInfo: firestoreBasicInfo,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // Update local state
      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              basicInfo: {
                ...currentProfile.basicInfo,
                ...basicInfo,
              },
            }
          : null,
      });
    } catch (error) {
      console.error('Error updating basic info:', error);
      set({ error: '基本情報の保存に失敗しました' });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  updatePhysicalInfo: async (userId: string, physicalInfo: Partial<ProfilePhysicalInfo>) => {
    try {
      set({ isSaving: true, error: null });
      
      const profileRef = doc(db, 'profiles', userId);
      const currentProfile = get().profile;

      // Check if document exists
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        await updateDoc(profileRef, {
          physicalInfo: {
            ...currentProfile?.physicalInfo,
            ...physicalInfo,
          },
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(profileRef, {
          userId,
          basicInfo: {},
          physicalInfo,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              physicalInfo: {
                ...currentProfile.physicalInfo,
                ...physicalInfo,
              },
            }
          : null,
      });
    } catch (error) {
      console.error('Error updating physical info:', error);
      set({ error: '身体情報の保存に失敗しました' });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  updateDonationPolicy: async (userId: string, donationPolicy: Partial<ProfileDonationPolicy>) => {
    try {
      set({ isSaving: true, error: null });
      
      const profileRef = doc(db, 'profiles', userId);
      const currentProfile = get().profile;

      // Check if document exists
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        await updateDoc(profileRef, {
          donationPolicy: {
            ...currentProfile?.donationPolicy,
            ...donationPolicy,
          },
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(profileRef, {
          userId,
          basicInfo: {},
          donationPolicy,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              donationPolicy: {
                ...currentProfile.donationPolicy,
                ...donationPolicy,
              },
            }
          : null,
      });
    } catch (error) {
      console.error('Error updating donation policy:', error);
      set({ error: '提供方針の保存に失敗しました' });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  updateDoneePolicy: async (userId: string, doneePolicy: Partial<ProfileDoneePolicy>) => {
    try {
      set({ isSaving: true, error: null });
      
      const profileRef = doc(db, 'profiles', userId);
      const currentProfile = get().profile;

      // Check if document exists
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        // Use dot notation for partial updates
        const updates: Record<string, unknown> = {
          updatedAt: serverTimestamp(),
        };
        
        if (doneePolicy.methods !== undefined) {
          updates['doneePolicy.methods'] = doneePolicy.methods;
        }
        if (doneePolicy.preferredLocation !== undefined) {
          updates['doneePolicy.preferredLocation'] = doneePolicy.preferredLocation;
        }
        
        await updateDoc(profileRef, updates);
      } else {
        await setDoc(profileRef, {
          userId,
          basicInfo: {},
          doneePolicy,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              doneePolicy: {
                ...currentProfile.doneePolicy,
                ...doneePolicy,
              },
            }
          : null,
      });
    } catch (error) {
      console.error('Error updating donee policy:', error);
      set({ error: '依頼方針の保存に失敗しました' });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  // 自由記入欄は公開プロフィール（profiles）に保存
  updateFreeText: async (userId: string, freeText: string) => {
    try {
      set({ isSaving: true, error: null });
      
      const profileRef = doc(db, 'profiles', userId);
      const currentProfile = get().profile;

      // Check if document exists
      const profileSnap = await getDoc(profileRef);
      if (profileSnap.exists()) {
        await updateDoc(profileRef, {
          freeText,
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(profileRef, {
          userId,
          basicInfo: {},
          freeText,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              freeText,
            }
          : null,
      });
    } catch (error) {
      console.error('Error updating free text:', error);
      set({ error: '自由記入欄の保存に失敗しました' });
      throw error;
    } finally {
      set({ isSaving: false });
    }
  },

  // 公開画像（アイコン）を /users/{userId}/public/ にアップロード
  uploadProfileImage: async (userId: string, file: File) => {
    try {
      set({ isUploading: true, error: null });

      // Use a unique object name so replacing an avatar always produces a fresh download URL.
      const extFromName = file.name.includes('.') ? file.name.split('.').pop() : undefined;
      const extFromType = file.type.split('/')[1];
      const ext = extFromName || extFromType || 'jpg';
      const storageRef = ref(storage, `users/${userId}/public/icon-${Date.now()}.${ext}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Update user document with icon URL
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        iconUrl: downloadUrl,
        updatedAt: serverTimestamp(),
      });

      return downloadUrl;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      set({ error: 'プロフィール画像のアップロードに失敗しました' });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  // 非公開画像（追加写真）を /users/{userId}/private/ にアップロード
  // privateProfilesコレクションに保存
  uploadAdditionalImage: async (userId: string, file: File) => {
    try {
      set({ isUploading: true, error: null });

      const currentProfile = get().profile;
      const currentImages = currentProfile?.images || [];

      if (currentImages.length >= 5) {
        throw new Error('追加画像は5枚までです');
      }

      // Create unique filename
      const ext = file.name.split('.').pop();
      const imageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const storageRef = ref(storage, `users/${userId}/private/${imageId}.${ext}`);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Update privateProfiles with new image
      const privateProfileRef = doc(db, 'privateProfiles', userId);
      const newImages = [...currentImages, downloadUrl];
      
      // Check if document exists
      const privateProfileSnap = await getDoc(privateProfileRef);
      if (privateProfileSnap.exists()) {
        await updateDoc(privateProfileRef, {
          images: newImages,
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(privateProfileRef, {
          userId,
          images: newImages,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              images: newImages,
            }
          : null,
      });

      return downloadUrl;
    } catch (error) {
      console.error('Error uploading additional image:', error);
      const message = error instanceof Error ? error.message : '画像のアップロードに失敗しました';
      set({ error: message });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  removeAdditionalImage: async (userId: string, imageUrl: string) => {
    try {
      set({ isUploading: true, error: null });

      const currentProfile = get().profile;
      const currentImages = currentProfile?.images || [];

      // Remove from storage
      try {
        const storageRef = ref(storage, imageUrl);
        await deleteObject(storageRef);
      } catch (storageError) {
        // Image might not exist in storage, continue anyway
        console.warn('Could not delete image from storage:', storageError);
      }

      // Update privateProfiles
      const privateProfileRef = doc(db, 'privateProfiles', userId);
      const newImages = currentImages.filter((url) => url !== imageUrl);
      
      await updateDoc(privateProfileRef, {
        images: newImages,
        updatedAt: serverTimestamp(),
      });

      set({
        profile: currentProfile
          ? {
              ...currentProfile,
              images: newImages,
            }
          : null,
      });
    } catch (error) {
      console.error('Error removing image:', error);
      set({ error: '画像の削除に失敗しました' });
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  clearError: () => set({ error: null }),
  
  reset: () => set({
    profile: null,
    isLoading: false,
    isSaving: false,
    isUploading: false,
    error: null,
  }),
}));
